import { FunctionalityEnum, UserRoleEnum } from '@elearning-platform/api-types/enums';
import { IconName, SideMenuItem, StickyMenuItem } from '@elearning-platform/ui';
import { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { Location, useLocation } from 'react-router';
import Cookies from 'universal-cookie';

import { phpApi } from '../api';
import { AppConfig } from '../appConfig';
import { menuPositions } from '../consts';
import { RouteNameEnum } from '../enums';
import { LangRouteInfo, MenuPosition } from '../interfaces';
import { UseAuth, UseMenuItems, UseRedirectTo } from '../interfaces/hooks';
import { getRouteNameByUrl, getUrlByRouteName } from '../utils';

import { useAuth } from './useAuth.hook';
import { useRedirectTo } from './useRedirectTo.hook';

export const useMenuItems: () => UseMenuItems = (): UseMenuItems => {
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { pathname }: Location = useLocation();
  const { appSettings, hasUserRequiredFunctionalities, isAuthInitialized, isRouteAvailableForMenuRole }: UseAuth = useAuth();

  const handleRedirect: () => void = useCallback((): void => {
    void phpApi.post('/api/user/management', {}).then(({ data }: AxiosResponse): void => {
      const cookies: Cookies = new Cookies();

      Object.keys(data.cookies).forEach((element: string): void => {
        cookies.set(element, data.cookies[element], { path: '/' });
      });

      redirect({ externalUrl: `${AppConfig.api}${data.redirect}`, isExternalUrl: true, inNewTab: true });
    });
  }, [redirect]);

  // for custom routes on forks
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const replaceParamsFromApi: (pathname: RouteNameEnum) => string = useCallback((pathname: RouteNameEnum): string => '', []);

  const isPublicRoute: (routeObject: LangRouteInfo) => boolean = (routeObject: LangRouteInfo): boolean => (
    !routeObject.requiredFunctionalities
    || ('operator' in routeObject.requiredFunctionalities && !routeObject.requiredFunctionalities.functionalities.length)
    || (Array.isArray(routeObject.requiredFunctionalities) && !routeObject.requiredFunctionalities.length)
  );

  const mainPositions: SideMenuItem[] = useMemo(
    (): SideMenuItem[] => [
      ...menuPositions
        .filter((item: MenuPosition): boolean => {
          const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

          if (!routeObject || isPublicRoute(routeObject)) {
            return true;
          }

          return (
            isAuthInitialized
            && !!appSettings?.currentMenuRole
            && hasUserRequiredFunctionalities(routeObject.requiredFunctionalities)
            && isRouteAvailableForMenuRole(item.pathname, appSettings.currentMenuRole)
          );
        })
        .map((item: MenuPosition): SideMenuItem => {
          const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

          return {
            name: item.pathname,
            label: t(item.label),
            internalPath: !routeObject?.url.includes(':')
              ? routeObject?.url
              : replaceParamsFromApi(item.pathname),
            icon: item.icon,
          };
        }),
      ...(
        hasUserRequiredFunctionalities([FunctionalityEnum.ManagementActionList])
        && appSettings?.currentMenuRole !== UserRoleEnum.USER
      )
        ? [{
            name: 'management',
            label: t('app.mainNavigation.management'),
            icon: IconName.PageManagement,
            action: handleRedirect,
          }]
        : [],
    ],
    [
      t,
      isAuthInitialized,
      appSettings?.currentMenuRole,
      handleRedirect,
      hasUserRequiredFunctionalities,
      replaceParamsFromApi,
      isRouteAvailableForMenuRole,
    ]
  );

  const stickyMobileMenuPositions: StickyMenuItem[] = useMemo(
    (): StickyMenuItem[] => menuPositions
      .filter((item: MenuPosition): boolean => {
        const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

        if (!item.renderOnStickyMobileMenu) {
          return false;
        }
        if (!routeObject || isPublicRoute(routeObject)) {
          return true;
        }

        return isAuthInitialized && hasUserRequiredFunctionalities(routeObject.requiredFunctionalities);
      })
      .map((item: MenuPosition): StickyMenuItem => {
        const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

        return {
          name: item.pathname,
          path: !routeObject?.url.includes(':')
            ? routeObject?.url ?? '/'
            : replaceParamsFromApi(item.pathname),
          icon: item.icon,
        };
      }),
    [isAuthInitialized, hasUserRequiredFunctionalities, replaceParamsFromApi]
  );

  const currentMenuPositionName: RouteNameEnum | undefined = useMemo(
    (): RouteNameEnum | undefined => {
      const currentRouteName: RouteNameEnum | undefined = getRouteNameByUrl(pathname);
      if (!currentRouteName) {
        return undefined;
      }

      return mainPositions.find((item: SideMenuItem): boolean => {
        const positionUrl: string | undefined = getUrlByRouteName(item.name as RouteNameEnum)?.url;

        return !!positionUrl && !!getUrlByRouteName(currentRouteName)?.url.startsWith(positionUrl);
      })?.name as RouteNameEnum;
    },
    [mainPositions, pathname]
  );

  return {
    currentMenuPositionName,
    mainPositions,
    stickyMobileMenuPositions,
  };
};
